const CURRENCY_CONFIG = {
  XOF: { symbol: 'FCFA', locale: 'fr-SN', decimals: 0, hasTVA: false, after: true },
  EUR: { symbol: '€',    locale: 'fr-FR', decimals: 2, hasTVA: true,  tvaRate: 0.20, after: true },
  USD: { symbol: '$',    locale: 'en-US', decimals: 2, hasTVA: false, after: false },
};

function fmtAmount(amount, devise) {
  const c = CURRENCY_CONFIG[devise] || CURRENCY_CONFIG.XOF;
  const n = new Intl.NumberFormat(c.locale, {
    minimumFractionDigits: c.decimals,
    maximumFractionDigits: c.decimals,
  }).format(amount);
  return c.after ? `${n}&nbsp;${c.symbol}` : `${c.symbol}${n}`;
}

function fmtDate(d) {
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
}

function fmtTime(d) {
  return new Date(d).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

function resolveTVA(devise, salon) {
  if (devise === 'EUR') return { hasTVA: true, rate: 0.20 };
  if (salon?.tvaEnabled) return { hasTVA: true, rate: (salon.tauxTva ?? 18) / 100 };
  return { hasTVA: false };
}

function buildTVASection(montant, devise, salon) {
  const { hasTVA, rate } = resolveTVA(devise, salon);
  if (!hasTVA) return '';
  const ht  = montant / (1 + rate);
  const tva = montant - ht;
  const pct = (rate * 100).toFixed(0);
  return `
    <tr class="sub">
      <td colspan="2" class="sub-label">Montant HT</td>
      <td class="sub-val">${fmtAmount(ht, devise)}</td>
    </tr>
    <tr class="sub">
      <td colspan="2" class="sub-label">TVA (${pct}&nbsp;%)</td>
      <td class="sub-val">${fmtAmount(tva, devise)}</td>
    </tr>`;
}

export function printInvoice({ transaction: tx, salon }) {
  const devise  = salon?.devise || 'XOF';
  const inv     = tx.invoiceNumber || '-';
  const client  = tx.clientNom || '';
  const desc    = tx.description || tx.type || '';
  const method  = tx.methodePaiement || 'Espèces';

  const html = `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8"/>
  <title>Facture ${inv}</title>
  <style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    body {
      font-family: 'Helvetica Neue', Arial, sans-serif;
      background: #fff;
      color: #111;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .page {
      width: 210mm;
      min-height: 297mm;
      margin: 0 auto;
      padding: 0;
      display: flex;
      flex-direction: column;
    }

    /* ── Header ── */
    .header {
      background: #0f0f0f;
      padding: 44px 52px 36px;
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
    }
    .salon-block .label {
      font-size: 9px;
      font-weight: 700;
      letter-spacing: 0.28em;
      text-transform: uppercase;
      color: rgba(255,255,255,0.38);
      margin-bottom: 8px;
    }
    .salon-name {
      font-size: 26px;
      font-weight: 900;
      color: #fff;
      letter-spacing: -0.03em;
      line-height: 1;
    }
    .salon-sub {
      font-size: 12px;
      color: rgba(255,255,255,0.38);
      margin-top: 8px;
      line-height: 1.7;
    }
    .inv-block { text-align: right; }
    .inv-number {
      font-size: 28px;
      font-weight: 900;
      color: #fff;
      letter-spacing: -0.03em;
    }
    .inv-date {
      font-size: 12px;
      color: rgba(255,255,255,0.38);
      margin-top: 6px;
    }

    /* ── Gold rule ── */
    .gold-rule {
      height: 3px;
      background: linear-gradient(90deg, #b8892f, #e2c278, #b8892f);
    }

    /* ── Body ── */
    .body { padding: 44px 52px; flex: 1; }

    /* ── Info row ── */
    .info-row {
      display: flex;
      gap: 0;
      margin-bottom: 40px;
      border: 1px solid #efefef;
      border-radius: 10px;
      overflow: hidden;
    }
    .info-cell {
      flex: 1;
      padding: 20px 24px;
    }
    .info-cell + .info-cell { border-left: 1px solid #efefef; }
    .cell-label {
      font-size: 9px;
      font-weight: 700;
      letter-spacing: 0.22em;
      text-transform: uppercase;
      color: #9ca3af;
      margin-bottom: 6px;
    }
    .cell-value {
      font-size: 15px;
      font-weight: 800;
      color: #111;
    }
    .cell-sub {
      font-size: 12px;
      color: #6b7280;
      margin-top: 3px;
      line-height: 1.5;
    }

    /* ── Table ── */
    table { width: 100%; border-collapse: collapse; margin-bottom: 0; }
    thead th {
      font-size: 9px;
      font-weight: 700;
      letter-spacing: 0.2em;
      text-transform: uppercase;
      color: #9ca3af;
      padding: 0 0 14px;
      border-bottom: 2px solid #111;
      text-align: left;
    }
    thead th.r { text-align: right; }
    tbody td {
      padding: 20px 0;
      border-bottom: 1px solid #f3f4f6;
      vertical-align: top;
    }
    td.desc-main { font-size: 14px; font-weight: 700; color: #111; }
    td.desc-sub  { font-size: 11px; color: #9ca3af; font-weight: 600; text-transform: uppercase; letter-spacing: 0.1em; margin-top: 4px; }
    td.amount    { text-align: right; font-size: 15px; font-weight: 800; color: #111; }
    tr.sub td    { padding: 6px 0; border-bottom: none; }
    td.sub-label { font-size: 12px; color: #9ca3af; text-align: right; padding-right: 32px; }
    td.sub-val   { font-size: 12px; color: #9ca3af; text-align: right; font-weight: 600; }

    /* ── Total box ── */
    .total-wrap { display: flex; justify-content: flex-end; margin-top: 20px; }
    .total-box {
      background: #0f0f0f;
      border-radius: 10px;
      padding: 20px 28px;
      display: flex;
      align-items: center;
      gap: 40px;
      min-width: 280px;
      justify-content: space-between;
    }
    .total-label {
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.22em;
      text-transform: uppercase;
      color: rgba(255,255,255,0.45);
    }
    .total-amount {
      font-size: 24px;
      font-weight: 900;
      color: #fff;
      letter-spacing: -0.03em;
    }

    /* ── Footer ── */
    .footer {
      border-top: 1px solid #f3f4f6;
      padding: 24px 52px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: auto;
    }
    .footer-left { font-size: 12px; color: #9ca3af; }
    .footer-right {
      font-size: 11px;
      font-weight: 700;
      color: #6b7280;
      text-transform: uppercase;
      letter-spacing: 0.15em;
    }

    /* ── Print ── */
    @media print {
      .page { margin: 0; }
      @page { size: A4; margin: 0; }
    }

    @media screen {
      body { background: #f4f4f0; padding: 32px 16px; }
      .page { box-shadow: 0 8px 48px rgba(0,0,0,0.12); }
    }
  </style>
</head>
<body>
<div class="page">

  <!-- Header -->
  <div class="header">
    <div class="salon-block">
      <div class="label">Facture émise par</div>
      <div class="salon-name">${salon?.nom || 'Salon'}</div>
      <div class="salon-sub">
        ${salon?.adresse ? salon.adresse + '<br/>' : ''}
        ${salon?.telephone ? salon.telephone : ''}
      </div>
    </div>
    <div class="inv-block">
      <div class="inv-number">${inv}</div>
      <div class="inv-date">${fmtDate(tx.date)} - ${fmtTime(tx.date)}</div>
    </div>
  </div>

  <!-- Gold accent -->
  <div class="gold-rule"></div>

  <!-- Body -->
  <div class="body">

    <!-- Info row -->
    <div class="info-row">
      ${client ? `
      <div class="info-cell">
        <div class="cell-label">Client</div>
        <div class="cell-value">${client}</div>
      </div>` : ''}
      <div class="info-cell">
        <div class="cell-label">Paiement</div>
        <div class="cell-value">${method}</div>
      </div>
      <div class="info-cell">
        <div class="cell-label">Type</div>
        <div class="cell-value">${tx.type}</div>
        ${tx.categorie ? `<div class="cell-sub">${tx.categorie}</div>` : ''}
      </div>
    </div>

    <!-- Table -->
    <table>
      <thead>
        <tr>
          <th colspan="2">Description</th>
          <th class="r">Montant</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td colspan="2">
            <div class="desc-main">${desc}</div>
          </td>
          <td class="amount">${fmtAmount(tx.montant, devise)}</td>
        </tr>
        ${buildTVASection(tx.montant, devise, salon)}
      </tbody>
    </table>

    <!-- Total -->
    <div class="total-wrap">
      <div class="total-box">
        <span class="total-label">${resolveTVA(devise, salon).hasTVA ? 'Total TTC' : 'Total'}</span>
        <span class="total-amount">${fmtAmount(tx.montant, devise)}</span>
      </div>
    </div>

  </div>

  <!-- Footer -->
  <div class="footer">
    <div class="footer-left">Merci de votre confiance !</div>
    <div class="footer-right">${salon?.nom || ''}</div>
  </div>

</div>
<script>window.onload = () => window.print();</script>
</body>
</html>`;

  const win = window.open('', '_blank', 'width=860,height=1000');
  if (win) { win.document.write(html); win.document.close(); }
}
